import React, { Suspense, useMemo } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import {
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ApolloProvider } from "@apollo/client/react";
import apolloClient from "./graphql/apolloClient";
import Layout from "./components/Layout";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import { getRoleTheme } from "./themes/theme";
import { LoaderProvider } from "./context/LoaderContext";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import PageWithTitle from "./components/PageWithTitle";
import muiTheme from "./themes/muiTheme";
import { PublicRoute } from "./components/PublicRoute";
import RouteTransitionTracker from "./components/RouteTransitionTracker";

// ─── Lazy-loaded Pages ──────────────────────────────────────────────────────
const Login = React.lazy(() => import("./pages/Login"));
const Signup = React.lazy(() => import("./pages/Signup"));
const ForgetPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const AcceptInvitation = React.lazy(() => import("./pages/accept-invitation/AcceptInvitation"));
const Onboard = React.lazy(() => import("./pages/onBoard/Onboard"));
const TicketingBoard = React.lazy(() => import("./pages/ticketing"));
const Gallery = React.lazy(() => import("./pages/gallery"));
const SubscriptionOverview = React.lazy(() => import("./pages/subscription/SubscriptionOverview"));
const Team = React.lazy(() => import("./pages/team/Team"));
const Partners = React.lazy(() => import("./pages/partners/Partners"));
const Company = React.lazy(() => import("./pages/company/Company"));
const AiAddons = React.lazy(() => import("./pages/ai-addons/AiAddons"));
const ProductCategory = React.lazy(() => import("./pages/masterData/productCategory/ProductCategory"));
const ProductCategoryDetails = React.lazy(() => import("./pages/masterData/productCategory/ProductCategoryDetails"));
const ProductType = React.lazy(() => import("./pages/masterData/productType/ProductType"));
const ProductTypeDetails = React.lazy(() => import("./pages/masterData/productType/ProductTypeDetails"));
const Product = React.lazy(() => import("./pages/masterData/products/Product"));
const ProductDetails = React.lazy(() => import("./pages/masterData/products/ProductDetails"));
const FAQs = React.lazy(() => import("./pages/masterData/faqs/FAQs"));
const Manufacturer = React.lazy(() => import("./pages/masterData/manufacturer/Manufacturer"));
const ServiceType = React.lazy(() => import("./pages/masterData/serviceType/ServiceType"));
const Vendor = React.lazy(() => import("./pages/masterData/vendor/Vendor"));
const WorkOrderStage = React.lazy(() => import("./pages/masterData/workorderStage/WorkOrderStage"));
const PromptLibrary = React.lazy(() => import("./pages/masterData/prompt-library/PromptLibraryPage"));
const ComplianceLenses = React.lazy(() => import("./pages/masterData/compliance-lenses/ComplianceLensesPage"));
const MasterObligations = React.lazy(() => import("./pages/masterData/masterObligations/MasterObligationsPage"));
const Plans = React.lazy(() => import("./pages/plans/Plans"));
const SettingsPage = React.lazy(() => import("./pages/settings/Settings"));
const RequestedProducts = React.lazy(() => import("./pages/requestedProducts/RequestedProducts"));
const MainDashboard = React.lazy(() => import("./pages/dashboard/MainDashboard"));

const muiCache = createCache({ key: "mui", prepend: true });
const chakraCache = createCache({ key: "chakra" });

function App() {
  const chakraTheme = getRoleTheme("user");
  const memoizedMuiTheme = useMemo(() => muiTheme, []);

  return (
    <ApolloProvider client={apolloClient}>
      <CacheProvider value={muiCache}>
        <StyledEngineProvider injectFirst>
          <ChakraProvider theme={chakraTheme}>
            <CacheProvider value={chakraCache}>
              <MuiThemeProvider theme={memoizedMuiTheme}>
                <BrowserRouter>
                  <RouteTransitionTracker />
                  <AuthProvider>
                    <LoaderProvider>
                      <Suspense fallback={null}>
                        <Routes>
                          <Route
                            path="/login"
                            element={
                              <PublicRoute>
                                <Login />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/signup"
                            element={
                              <PublicRoute>
                                <Signup />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/forgot-password"
                            element={
                              <PublicRoute>
                                <ForgetPassword />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/reset-password"
                            element={
                              <PublicRoute>
                                <ResetPassword />
                              </PublicRoute>
                            }
                          />
                          <Route path="/accept-invitation" element={<AcceptInvitation />} />

                          <Route
                            element={
                              <ProtectedRoute>
                                <Layout />
                              </ProtectedRoute>
                            }
                          >
                            <Route
                              path="/dashboard"
                              element={
                                <PageWithTitle title="Dashboard">
                                  <MainDashboard />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/onboard"
                              element={
                                <PageWithTitle title="Onboard">
                                  <Onboard />
                                </PageWithTitle>
                              }
                            />
                            <Route
                              path="/ticketing"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Ticketing">
                                    <TicketingBoard />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/gallery"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Gallery">
                                    <Gallery />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/subscription"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Plan & Billing">
                                    <SubscriptionOverview />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/team"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Team">
                                    <Team />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/partners"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                                  <PageWithTitle title="Partners">
                                    <Partners />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/company"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Company">
                                    <Company />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/ai-addons"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="AI Addons">
                                    <AiAddons />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/plans"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                                  <PageWithTitle title="Subscription Plans">
                                    <Plans />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/settings"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                                  <PageWithTitle title="Profile">
                                    <SettingsPage />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />

                            <Route
                              path="/manufacturer"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Manufacturer">
                                    <Manufacturer />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/vendor"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Vendor">
                                    <Vendor />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/producttype"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Product Type">
                                    <ProductType />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/producttype/:id"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Product Type Details">
                                    <ProductTypeDetails />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/servicetype"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Service Type">
                                    <ServiceType />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/productcategory"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Product Category">
                                    <ProductCategory />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/productcategory/:id"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Product Category Details">
                                    <ProductCategoryDetails />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/products"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Products">
                                    <Product />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/products/:id"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Product Details">
                                    <ProductDetails />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/prompt-library"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Prompt Library">
                                    <PromptLibrary />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/lens"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Lenses">
                                    <ComplianceLenses />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/obligations"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Obligations">
                                    <MasterObligations />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/faqs"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="FAQs">
                                    <FAQs />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/workorderstage"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN", "partner_admin"]}>
                                  <PageWithTitle title="Work Order Stages">
                                    <WorkOrderStage />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                            <Route
                              path="/requested-products"
                              element={
                                <RoleProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                                  <PageWithTitle title="Requested Products">
                                    <RequestedProducts />
                                  </PageWithTitle>
                                </RoleProtectedRoute>
                              }
                            />
                          </Route>

                          <Route
                            path="/"
                            element={
                              <Navigate
                                to={localStorage.getItem("accessToken") ? "/dashboard" : "/login"}
                                replace
                              />
                            }
                          />
                        </Routes>
                      </Suspense>
                    </LoaderProvider>
                  </AuthProvider>
                </BrowserRouter>
              </MuiThemeProvider>
            </CacheProvider>
          </ChakraProvider>
        </StyledEngineProvider>
      </CacheProvider>
    </ApolloProvider>
  );
}

export default App;
